import React from 'react';
import { Box, FormHelperText, Paper, Stack } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FilePond, registerPlugin } from 'react-filepond';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginImageResize from 'filepond-plugin-image-resize';
import FilePondPluginImageTransform from 'filepond-plugin-image-transform';
import FilePondPluginImageCrop from 'filepond-plugin-image-crop';
import 'filepond/dist/filepond.min.css';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css';
import { EditButton } from '../button';
const useStyles = makeStyles({
    '@global': {
        // The color value of this element is black by default, which draws shadows over
        // the bottom of the preview image. Remove it by setting transparent.
        '.filepond--image-preview-overlay-idle': {
            color: 'transparent',
        },
    },
});
registerPlugin(FilePondPluginImagePreview, FilePondPluginFileValidateType, FilePondPluginFileValidateSize, FilePondPluginImageResize, FilePondPluginImageTransform, FilePondPluginImageCrop);
const EditableImageField = ({ aspectRatio = '1:1', editable = false, locked: locked_ = false, helperTexts, url, maxHeight, maxWidth, onSave, onValueChanged, ...props }) => {
    useStyles();
    const [locked, setLocked] = React.useState(locked_);
    const [editDisabled, setEditDisabled] = React.useState(false);
    const [blobData, setBlobData] = React.useState();
    const [error, setError] = React.useState();
    const handleEdit = () => setLocked(false);
    const handleSubmit = () => {
        setLocked(true);
        setEditDisabled(true);
        onSave?.(blobData);
        setEditDisabled(false);
    };
    const ref = React.useCallback(async (filePond) => {
        // Note that filePond is null in unit tests.
        if (filePond?.removeFiles) {
            filePond.removeFiles();
            if (url) {
                // Filepond's addFile() converts % in url into %25 before loading an image.
                // It converts a url-encoded slash (%2F) tp %252f, which results in error.
                // As a workaround, load an image as a blob and pass it to addFile().
                const r = await fetch(url, { mode: 'cors', credentials: 'omit', cache: 'no-cache' });
                const blob = await r.blob();
                filePond.addFile(blob);
            }
        }
    }, [url]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Stack, { direction: "row" },
            React.createElement(Box, { sx: { width: '100%', aspectRatio: 1 / 1, position: 'relative' } },
                !locked ? (React.createElement(FilePond, { ref: ref, maxFiles: 1, imageCropAspectRatio: aspectRatio, stylePanelLayout: "integrated circle", stylePanelAspectRatio: aspectRatio, styleLoadIndicatorPosition: "left", 
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    onpreparefile: (file, output) => {
                        const blobData = {
                            blob: output,
                            filename: file.filename,
                            size: file.fileSize,
                            mimeType: file.fileType,
                            ext: file.fileExtension,
                        };
                        setBlobData(blobData);
                        onValueChanged?.(blobData);
                    }, onremovefile: () => setBlobData(undefined), acceptedFileTypes: ['image/jpeg', 'image/png'], onerror: setError, imagePreviewTransparencyIndicator: "grid", imageResizeTargetWidth: maxWidth, imageResizeTargetHeight: maxHeight, imagePreviewMarkupShow: false, onaddfilestart: () => setEditDisabled(true), onaddfile: () => setEditDisabled(false), ...props })) : !!url ? (React.createElement("img", { src: url, loading: "lazy", style: { width: '100%', aspectRatio: 1, backgroundColor: 'rgba(255,255,255,0.01)' } })) : (React.createElement("div", { style: { width: '100%', aspectRatio: 1, backgroundColor: 'rgba(255,255,255,0.01)' } })),
                React.createElement(Paper, { variant: "outlined", sx: {
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        zIndex: 1,
                    } }, editable ? (React.createElement(EditButton, { disabled: editDisabled, locked: locked, onEdit: handleEdit, onSave: handleSubmit })) : null))),
        React.createElement(Stack, null,
            helperTexts
                ? helperTexts.map((e, i) => (React.createElement(FormHelperText, { key: i, sx: { mx: 0 } }, e)))
                : null,
            error && 'main' in error ? (React.createElement(FormHelperText, { sx: { mx: 0 }, error: true }, error['main'])) : null,
            error && 'sub' in error ? (React.createElement(FormHelperText, { sx: { mx: 0 }, error: true }, error['sub'])) : null)));
};
export default EditableImageField;
